import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const QuickAssessment = ({ onAssessmentComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [assessmentData, setAssessmentData] = useState({
    organizationSize: '',
    industry: '',
    currentAIUsage: '',
    primaryGoal: '',
    timeline: '',
    budget: '',
    email: '',
    company: ''
  });

  const totalSteps = 5;

  const organizationSizes = [
    { value: 'startup', label: 'Startup (1-50 employees)' },
    { value: 'small', label: 'Small Business (51-200 employees)' },
    { value: 'medium', label: 'Medium Enterprise (201-1000 employees)' },
    { value: 'large', label: 'Large Enterprise (1000+ employees)' }
  ];

  const industries = [
    { value: 'technology', label: 'Technology & Software' },
    { value: 'finance', label: 'Financial Services' },
    { value: 'healthcare', label: 'Healthcare & Life Sciences' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'retail', label: 'Retail & E-commerce' },
    { value: 'consulting', label: 'Professional Services' },
    { value: 'other', label: 'Other' }
  ];

  const aiUsageLevels = [
    { value: 'none', label: 'No current AI implementation' },
    { value: 'basic', label: 'Basic AI tools (ChatGPT, etc.)' },
    { value: 'moderate', label: 'Some AI integration in workflows' },
    { value: 'advanced', label: 'Advanced AI systems in production' }
  ];

  const primaryGoals = [
    { value: 'cost-reduction', label: 'Reduce operational costs' },
    { value: 'efficiency', label: 'Improve operational efficiency' },
    { value: 'innovation', label: 'Drive product innovation' },
    { value: 'competitive-advantage', label: 'Gain competitive advantage' },
    { value: 'vendor-independence', label: 'Achieve vendor independence' }
  ];

  const timelines = [
    { value: 'immediate', label: 'Immediate (0-3 months)' },
    { value: 'short', label: 'Short-term (3-6 months)' },
    { value: 'medium', label: 'Medium-term (6-12 months)' },
    { value: 'long', label: 'Long-term (12+ months)' }
  ];

  const budgetRanges = [
    { value: 'under-50k', label: 'Under $50,000' },
    { value: '50k-250k', label: '$50,000 - $250,000' },
    { value: '250k-1m', label: '$250,000 - $1,000,000' },
    { value: 'over-1m', label: 'Over $1,000,000' }
  ];

  const handleInputChange = (field, value) => {
    setAssessmentData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      onAssessmentComplete(assessmentData);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return assessmentData?.organizationSize && assessmentData?.industry;
      case 2:
        return assessmentData?.currentAIUsage;
      case 3:
        return assessmentData?.primaryGoal;
      case 4:
        return assessmentData?.timeline && assessmentData?.budget;
      case 5:
        return assessmentData?.email && assessmentData?.company;
      default:
        return false;
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-brand-primary mb-4">
                Tell us about your organization
              </h3>
              <Select
                label="Organization Size"
                options={organizationSizes}
                value={assessmentData?.organizationSize}
                onChange={(value) => handleInputChange('organizationSize', value)}
                placeholder="Select organization size"
              />
            </div>
            <div>
              <Select
                label="Industry"
                options={industries}
                value={assessmentData?.industry}
                onChange={(value) => handleInputChange('industry', value)}
                placeholder="Select your industry"
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-brand-primary mb-4">
                What's your current AI maturity level?
              </h3>
              <Select
                label="Current AI Usage"
                options={aiUsageLevels}
                value={assessmentData?.currentAIUsage}
                onChange={(value) => handleInputChange('currentAIUsage', value)}
                placeholder="Select current AI usage level"
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-brand-primary mb-4">
                What's your primary goal with AI?
              </h3>
              <Select
                label="Primary Goal"
                options={primaryGoals}
                value={assessmentData?.primaryGoal}
                onChange={(value) => handleInputChange('primaryGoal', value)}
                placeholder="Select your primary goal"
              />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-brand-primary mb-4">
                Timeline and budget considerations
              </h3>
              <Select
                label="Implementation Timeline"
                options={timelines}
                value={assessmentData?.timeline}
                onChange={(value) => handleInputChange('timeline', value)}
                placeholder="Select timeline"
              />
            </div>
            <div>
              <Select
                label="Budget Range"
                options={budgetRanges}
                value={assessmentData?.budget}
                onChange={(value) => handleInputChange('budget', value)}
                placeholder="Select budget range"
              />
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-brand-primary mb-4">
                Contact information for your custom report
              </h3>
              <Input
                label="Company Name"
                type="text"
                value={assessmentData?.company}
                onChange={(e) => handleInputChange('company', e?.target?.value)}
                placeholder="Enter your company name"
              />
            </div>
            <div>
              <Input
                label="Email Address"
                type="email"
                value={assessmentData?.email}
                onChange={(e) => handleInputChange('email', e?.target?.value)}
                placeholder="Enter your email address"
                description="We'll send your custom AI readiness report to this email"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-brand-primary mb-4">
            5-Minute AI Readiness Assessment
          </h2>
          <p className="text-xl text-text-secondary">
            Get a custom recommendation report tailored to your organization's needs
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-executive p-8">
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-brand-primary">
                Step {currentStep} of {totalSteps}
              </span>
              <span className="text-sm text-text-secondary">
                {Math.round((currentStep / totalSteps) * 100)}% Complete
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-brand-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              />
            </div>
          </div>

          {/* Step Content */}
          <div className="min-h-[300px]">
            {renderStep()}
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
            <Button
              variant="ghost"
              onClick={handlePrevious}
              disabled={currentStep === 1}
              iconName="ArrowLeft"
              iconPosition="left"
            >
              Previous
            </Button>

            <div className="flex space-x-2">
              {Array.from({ length: totalSteps }, (_, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full ${
                    i + 1 <= currentStep ? 'bg-brand-primary' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>

            <Button
              variant="default"
              onClick={handleNext}
              disabled={!isStepValid()}
              className="bg-brand-primary"
              iconName={currentStep === totalSteps ? "Send" : "ArrowRight"}
              iconPosition="right"
            >
              {currentStep === totalSteps ? 'Get My Report' : 'Next'}
            </Button>
          </div>
        </div>

        <div className="text-center mt-8">
          <div className="flex items-center justify-center space-x-6 text-sm text-text-secondary">
            <div className="flex items-center">
              <Icon name="Shield" size={16} className="mr-2 text-green-500" />
              Secure & Confidential
            </div>
            <div className="flex items-center">
              <Icon name="Clock" size={16} className="mr-2 text-blue-500" />
              Takes 5 minutes
            </div>
            <div className="flex items-center">
              <Icon name="FileText" size={16} className="mr-2 text-purple-500" />
              Custom Report Generated
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuickAssessment;